import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';

import { TagSchemaField, Topic } from '@/core/notifications/model';
import {
  fieldHeaderLabel,
  fieldSentenceLabel,
  leafLabel,
} from '@/core/notifications/model/tag-schema';

// One row per tagSchema field: what it's set to ("All X" or the chosen
// values), with an optional "Edit" jump-back for whoever renders this.
// Reused as both the wizard's own scope-confirm step and the fixed summary a
// preloaded (module-embedded) flow shows above Step 3 — one component, one
// look, wherever a subscription's scope needs to be shown back to the user.
export default function ScopeSummary({
  topic,
  tagValues,
  scopeModeByField,
  missingRequiredTagKeys = [],
  invalidFieldKeys = [],
  onEditField,
}: {
  topic: Topic | undefined;
  tagValues: Record<string, string[]>;
  scopeModeByField: Record<string, 'all' | 'narrow'>;
  /** Fields still needing a value — highlighted in place of their "All X" line. */
  missingRequiredTagKeys?: string[];
  /** Fields whose stored value(s) no longer match the schema's allowed options. */
  invalidFieldKeys?: string[];
  /** Omit to render a read-only summary (no Edit links). */
  onEditField?: (tagKey: string) => void;
}) {
  const tagSchema = topic?.tagSchema ?? {};
  const fieldKeys = Object.keys(tagSchema);

  if (fieldKeys.length === 0) return null;

  return (
    <div
      className="rounded"
      style={{ border: '1px solid var(--bs-primary-200)', overflow: 'hidden' }}
    >
      {fieldKeys.map((tagKey, index) => {
        const field: TagSchemaField = tagSchema[tagKey];
        const mode = scopeModeByField[tagKey] ?? 'all';
        const values = tagValues[tagKey] ?? [];
        const fieldLabel = fieldHeaderLabel(field, tagKey);
        const fieldSentenceValue = fieldSentenceLabel(field, tagKey);
        const isMissing = missingRequiredTagKeys.includes(tagKey);
        const isInvalid = invalidFieldKeys.includes(tagKey);
        const hasProblem = isMissing || isInvalid;

        return (
          <div
            key={tagKey}
            className="d-flex align-items-start justify-content-between gap-2 px-3 py-2"
            style={{
              borderTop: index > 0 ? '1px solid var(--bs-primary-200)' : undefined,
              fontSize: 13,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div className="fw-semibold">{fieldLabel}</div>
              <div
                style={{
                  fontSize: 12,
                  color: hasProblem ? 'var(--bs-danger)' : 'var(--bs-text-dark)',
                  opacity: hasProblem ? 1 : 0.6,
                }}
              >
                {isMissing
                  ? 'Required — not set yet'
                  : isInvalid
                    ? "Contains a value that isn't valid for this topic"
                    : mode === 'all' || values.length === 0
                      ? `Every ${fieldSentenceValue}`
                      : values.map(value => leafLabel(field, value)).join(', ')}
              </div>
            </div>
            {onEditField && (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-decoration-none flex-shrink-0"
                onClick={() => onEditField(tagKey)}
                aria-label={`Edit ${fieldLabel}`}
              >
                <FontAwesomeIcon icon={faPencil} className="me-1" style={{ fontSize: 11 }} />
                Edit
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
